import React from 'react';

import { dashboardVesselScheduleApis } from '@apis/next/vesselScheduleApis';
import addIcon from '@assets/img/icons/add.svg';
import { Button } from '@components/Button';
import {
  ActionableDialog,
  ActionableDialogActions,
  ActionableDialogTitle,
} from '@components/Dialog';
import { IconButton } from '@components/IconButton';
import { TextInput } from '@components/NextForms/TextInput';
import { VerticalSpace } from '@components/VerticalSpace';
import { vesselScheduleStrings } from '@data/vesselSchedule';
import { useVesselAutoComplete } from '@hooks/next/useVesselAutoComplete';
import { useFormUtils } from '@hooks/useFormUtils';
import MuiIconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import { VesselScheduleItemForm, VesselScheduleItemsForm } from '@models/next/VesselSchedule';
import { empty, MobxZodObjectField } from '@monoid-dev/mobx-zod-form';
import { useForm } from '@monoid-dev/mobx-zod-form-react';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { useQueryClient } from '@tanstack/react-query';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { getFieldLabel } from '@utils/nextForms';
import { formatDate } from '@utils/types';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';

const strings = createStrings({
  ja: {
    addItemsTitle: '本船スケジュールを追加する',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  vesselScheduleAddItemsFormContent: {
    padding: '40px 30px 20px',
  },
  vesselScheduleAddItemsTable: {
    ...common.middleText,
    width: '100%',
    tableLayout: 'fixed',
    '& th': {
      textAlign: 'center',
      fontWeight: 'normal',
    },
    '& th:first-child': {
      width: 40,
    },
    '& th:last-child': {
      width: 32,
    },
    '& td': {
      verticalAlign: 'top',
    },
    '& td:first-child': {
      paddingTop: 7,
      textAlign: 'center',
    },
    '& td:last-child': {
      paddingTop: 7,
      textAlign: 'center',
    },
  },
}));

const VesselScheduleAddItemsRow: React.FC<{
  index: number;
  field: MobxZodObjectField<typeof VesselScheduleItemForm>;
  onRemove?: () => void;
}> = observer(({ index, field, onRemove }) => {
  const { fields } = field;

  const autoCompleteConfigs = useVesselAutoComplete(fields.vessel, fields.callsign);

  return (
    <tr>
      <td>{index + 1}</td>

      <td>
        <TextInput
          field={fields.vessel}
          autoCompleteConfig={autoCompleteConfigs.vessel}
          popperConfig={{ width: 'max-content' }}
          inputProps={{ placeholder: getFieldLabel(fields.vessel) }}
        />
      </td>

      <td>
        <TextInput
          field={fields.callsign}
          autoCompleteConfig={autoCompleteConfigs.callsign}
          popperConfig={{ width: 'max-content' }}
          inputProps={{ placeholder: getFieldLabel(fields.callsign) }}
        />
      </td>

      <td>
        <TextInput
          field={fields.voyage}
          inputProps={{ placeholder: getFieldLabel(fields.voyage) }}
        />
      </td>

      <td>
        <TextInput field={fields.pol} inputProps={{ placeholder: getFieldLabel(fields.pol) }} />
      </td>

      <td>
        <TextInput field={fields.pod} inputProps={{ placeholder: getFieldLabel(fields.pod) }} />
      </td>

      <td>
        <TextInput
          field={fields.etd}
          inputProps={{ placeholder: formatDate(dayjs())! }}
          withErrorMessages={false}
        />
      </td>

      <td>
        <TextInput
          field={fields.eta}
          inputProps={{ placeholder: formatDate(dayjs().add(1, 'day'))! }}
          withErrorMessages={false}
        />
      </td>

      <td>
        <MuiIconButton
          onClick={() => onRemove?.()}
          disabled={!onRemove}
          style={{ padding: 0, marginTop: -3 }}
        >
          <CloseIcon fontSize="small" />
        </MuiIconButton>
      </td>
    </tr>
  );
});

export interface VesselScheduleAddItemsDialogProps {
  open: boolean;
  onClose: () => void;
}

export const VesselScheduleAddItemsDialog: React.FC<VesselScheduleAddItemsDialogProps> = observer(
  (props) => {
    const { open, onClose } = props;

    const queryClient = useQueryClient();

    const classes = {
      ...useCommonStyles(),
      ...useStyles(),
    };

    const postVesselSchedule = dashboardVesselScheduleApis.usePost(
      '/dashboard/vessel-schedule',
      undefined,
      {
        onSuccess: () =>
          queryClient.invalidateQueries(
            dashboardVesselScheduleApis.getKeyByPath('get', '/dashboard/vessel-schedule'),
          ),
      },
    );

    const form = useForm(VesselScheduleItemsForm, {
      initialOutput: [empty],
    });

    const { renderAlertsAndSpinnerModal } = useFormUtils({ mutations: [postVesselSchedule] });

    return (
      <>
        <ActionableDialog open={open} onClose={onClose} size="lg">
          <ActionableDialogTitle textAlign="center" hideCloseButton>
            {strings.addItemsTitle}
          </ActionableDialogTitle>

          <form
            {...form.bindForm({
              async onSubmit(values) {
                await postVesselSchedule.mutateAsync(values);
                onClose();
              },
            })}
          >
            <div className={classes.vesselScheduleAddItemsFormContent}>
              {renderAlertsAndSpinnerModal()}
              <table className={classes.vesselScheduleAddItemsTable}>
                <thead>
                  <tr>
                    <th>{vesselScheduleStrings.no}</th>
                    <th>{vesselScheduleStrings.vessel}</th>
                    <th>{vesselScheduleStrings.callsign}</th>
                    <th>{vesselScheduleStrings.voyage}</th>
                    <th>{vesselScheduleStrings.pol}</th>
                    <th>{vesselScheduleStrings.pod}</th>
                    <th>{vesselScheduleStrings.etd}</th>
                    <th>{vesselScheduleStrings.eta}</th>
                    <th />
                  </tr>
                </thead>

                <tbody>
                  {form.root.elements.map((element, i) => (
                    <VesselScheduleAddItemsRow
                      index={i}
                      key={element.uniqueId}
                      field={element}
                      onRemove={form.root.length > 1 ? () => form.root.splice(i, 1, []) : undefined}
                    />
                  ))}
                </tbody>
              </table>
              <VerticalSpace height={16} />

              <IconButton
                icon={<img src={addIcon} alt="Add Icon" />}
                text={formStrings.add}
                type="button"
                onClick={() => form.root.push(empty)}
              />
            </div>
            <ActionableDialogActions justifyContent="center">
              <Button
                color="primary"
                size="small"
                shape="round"
                type="submit"
                text={formStrings.ok}
                style={{ minWidth: 80 }}
              />
            </ActionableDialogActions>
          </form>
        </ActionableDialog>
      </>
    );
  },
);
